import { MultiShipmentExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';

// ─── Config-driven helpers (not mock state) ──────────────────────────────────

/** Shopify renders the merchant's preset corner-radius bucket as a concrete pixel value. */
export const PILL_CORNER_RADIUS_PX: Record<MultiShipmentExtension.PillCornerRadius, number> = {
  none: 0,
  small: 4,
  medium: 8,
  large: 12,
};

/** Maps a semantic banner style to the Shopify Polaris banner variant + leading icon. */
export interface BannerVariant {
  variant: 'info' | 'success' | 'warning' | 'critical';
  icon: string;
}

export const BANNER_STYLE_TO_VARIANT: Record<MultiShipmentExtension.BannerStyle, BannerVariant> = {
  info: { variant: 'info', icon: 'more_information' },
  success: { variant: 'success', icon: 'more_information' },
  warning: { variant: 'warning', icon: 'more_information' },
  danger: { variant: 'critical', icon: 'more_information' },
};

/** Resolves the {quantity} / {name} / {number} tokens a merchant can type into a label. */
export function resolveLabelTokens(
  pattern: string,
  tokens: { quantity?: number | string; name?: string; number?: number | string },
): string {
  return (pattern ?? '')
    .replace(/\{quantity\}/g, String(tokens.quantity ?? ''))
    .replace(/\{name\}/g, tokens.name ?? '')
    .replace(/\{number\}/g, String(tokens.number ?? ''));
}

// ─── Single shared preview scenario ──────────────────────────────────────────
// Every preview block (recipient list, order-summary tags, add-recipient modal)
// derives what it renders from this one object, so the data stays consistent.

const PRODUCT_THUMBNAIL = 'assets/icons/checkout_content_default.png';

export const PREVIEW_MAX_ENTRIES = 2;

export interface PreviewGiftNote {
  number: number;
  to: string;
  message: string;
  assignments: PreviewAssignment[];
}

export const PREVIEW_GIFT_NOTES: PreviewGiftNote[] = [
  {
    number: 1,
    to: 'Emma Johnson',
    message: 'Happy birthday! Hope this one gets plenty of use.',
    assignments: [{ productId: 'artisan-chocolate-box', quantity: 2 }],
  },
  {
    number: 2,
    to: 'Liam Carter',
    message: 'Congratulations on the new place — enjoy the slopes.',
    assignments: [{ productId: 'spa-day-gift-set', quantity: 1 }],
  },
];

export interface PreviewProduct {
  id: string;
  name: string;
  variant: string;
  thumbnail: string;
  quantityInCart: number;
  price: string;
}

export interface PreviewAssignment {
  productId: string;
  quantity: number;
}

export interface PreviewRecipient {
  number: number;
  name: string;
  address: string;
  deliveryDate: string;
  shippingLabel: string;
  giftNote: string;
  assignments: PreviewAssignment[];
}

export interface PreviewShippingRate {
  name: string;
  price: number;
}

export const PREVIEW_SHIPPING_RATES: PreviewShippingRate[] = [
  { name: 'Standard', price: 10 },
  { name: 'Express', price: 20 },
  { name: 'Choose your date', price: 10 },
];

export interface MultiShipmentPreview {
  products: PreviewProduct[];
  recipients: PreviewRecipient[];
  orderTotals: {
    subtotalItems: number;
    subtotal: string;
    shipping: string;
    estimatedTaxes: string;
    total: string;
    currencyCode: string;
  };
  /** The recipient the modal is "adding" — fills the title pattern's {number}. */
  modalRecipientNumber: number;
}

export const MULTI_SHIPMENT_PREVIEW: MultiShipmentPreview = {
  products: [
    {
      id: 'artisan-chocolate-box',
      name: 'The Artisan Chocolate Box',
      variant: 'Gift wrap',
      thumbnail: PRODUCT_THUMBNAIL,
      quantityInCart: 3,
      price: '$12.00',
    },
    {
      id: 'spa-day-gift-set',
      name: 'The Spa Day Gift Set',
      variant: 'Lavender | Large',
      thumbnail: PRODUCT_THUMBNAIL,
      quantityInCart: 3,
      price: '$12.00',
    },
  ],
  recipients: [
    {
      number: 1,
      name: 'Emma Johnson',
      address: '123 William Street · New York, 10038',
      deliveryDate: 'Thu, May 21',
      shippingLabel: 'Standard shipping · $6.50',
      giftNote: 'Happy birthday! Hope this one gets plenty of use.',
      assignments: [
        { productId: 'artisan-chocolate-box', quantity: 2 },
        { productId: 'spa-day-gift-set', quantity: 1 },
      ],
    },
    {
      number: 2,
      name: 'Liam Carter',
      address: '88 Market Street · San Francisco, 94103',
      deliveryDate: 'Mon, May 25',
      shippingLabel: 'Express shipping · $12.00',
      giftNote: 'Congratulations on the new place — enjoy every minute.',
      assignments: [
        { productId: 'artisan-chocolate-box', quantity: 1 },
        { productId: 'spa-day-gift-set', quantity: 2 },
      ],
    },
  ],
  orderTotals: {
    subtotalItems: 2,
    subtotal: '$36.00',
    shipping: 'Enter shipping address',
    estimatedTaxes: '$1.80',
    total: '$37.80',
    currencyCode: 'CAD',
  },
  modalRecipientNumber: 2,
};

// ─── Derivations — every block reads the shared scenario above ────────────────

function findProduct(productId: string): PreviewProduct | undefined {
  return MULTI_SHIPMENT_PREVIEW.products.find((product) => product.id === productId);
}

/** Item chips shown on a recipient card — one per assigned product (thumbnail + quantity). */
export function recipientItemChips(recipient: PreviewRecipient): { thumbnail: string; quantity: number }[] {
  return itemChips(recipient.assignments);
}

/** Item chips shown on a gift note card — the items that note travels with. */
export function giftNoteItemChips(note: PreviewGiftNote): { thumbnail: string; quantity: number }[] {
  return itemChips(note.assignments);
}

function itemChips(assignments: PreviewAssignment[]): { thumbnail: string; quantity: number }[] {
  return assignments.map((assignment) => ({
    thumbnail: findProduct(assignment.productId)?.thumbnail ?? PRODUCT_THUMBNAIL,
    quantity: assignment.quantity,
  }));
}

export interface ProductAssignment {
  quantity: number;
  recipientName: string;
  recipientNumber: number;
}

/** Who a product was assigned to — drives the "assigned" pills in the order summary. */
export function productAssignments(product: PreviewProduct): ProductAssignment[] {
  const assignments: ProductAssignment[] = [];
  MULTI_SHIPMENT_PREVIEW.recipients.forEach((recipient) => {
    recipient.assignments
      .filter((assignment) => assignment.productId === product.id)
      .forEach((assignment) => {
        assignments.push({
          quantity: assignment.quantity,
          recipientName: recipient.name,
          recipientNumber: recipient.number,
        });
      });
  });
  return assignments;
}

/** Cart quantity of a product that no recipient has claimed yet. */
export function unassignedQuantity(product: PreviewProduct): number {
  const assigned = productAssignments(product).reduce((total, assignment) => total + assignment.quantity, 0);
  return Math.max(0, product.quantityInCart - assigned);
}

/** Total items across the cart still waiting to be assigned to one of the given recipients. */
export function itemsStillToAssign(recipients: PreviewRecipient[]): number {
  return MULTI_SHIPMENT_PREVIEW.products.reduce((total, product) => total + remainingQuantity(product, recipients), 0);
}

function remainingQuantity(product: PreviewProduct, recipients: PreviewRecipient[]): number {
  const assigned = recipients
    .flatMap((recipient) => recipient.assignments)
    .filter((assignment) => assignment.productId === product.id)
    .reduce((quantity, assignment) => quantity + assignment.quantity, 0);
  return Math.max(0, product.quantityInCart - assigned);
}

/** The add card a block should show: the "add another" copy only once an entry exists and the merchant enabled it. */
export function resolveAddCard(
  firstCard: MultiShipmentExtension.ActionCard,
  addAnotherCard: MultiShipmentExtension.AddAnotherCard,
  existingCount: number,
): MultiShipmentExtension.ActionCard {
  if (existingCount > 0 && addAnotherCard?.enabled) {
    return addAnotherCard;
  }
  return firstCard;
}

export interface ModalItemView {
  name: string;
  subtitle: string;
  thumbnail: string;
  cartQuantity: number;
  available: boolean;
}

/** Products offered in the modal's "Which items?" step, as if only the first recipient had been added. */
export function modalItems(): ModalItemView[] {
  const alreadyAddedRecipients = MULTI_SHIPMENT_PREVIEW.recipients.slice(0, 1);
  return MULTI_SHIPMENT_PREVIEW.products.map((product) => {
    const remaining = remainingQuantity(product, alreadyAddedRecipients);
    const assigned = product.quantityInCart - remaining;
    return {
      name: product.name,
      subtitle: remaining > 0 ? `${remaining} more available` : `${product.variant} | ${assigned}x Assigned`,
      thumbnail: product.thumbnail,
      cartQuantity: product.quantityInCart,
      available: remaining > 0,
    };
  });
}
